import React, { useState, useEffect } from 'react';
import { useToast } from './Toast';
import Icon from './Icon';
import { C, S } from '../utils/styles';
import TaxSnapshotDetailModal from './admin/TaxSnapshotDetailModal';

export default function TaxSyncNotificationBanner() {
  const addToast = useToast();
  const [notifications, setNotifications] = useState([]);
  const [dismissed, setDismissed] = useState([]);
  const [selectedSnapshotId, setSelectedSnapshotId] = useState(null);

  useEffect(() => {
    fetchNotifications();
  }, []);

  const fetchNotifications = async () => {
    try {
      const token = localStorage.getItem('healthcues_token');
      const backendUrl = import.meta.env.REACT_APP_BACKEND_URL || process.env.REACT_APP_BACKEND_URL;
      const response = await fetch(`${backendUrl}/api/admin/notifications`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.ok) {
        const data = await response.json();
        setNotifications(data);
      }
    } catch (err) {
      console.error('Failed to fetch notifications:', err);
    }
  };

  const handleDismiss = async (notificationId) => {
    try {
      const token = localStorage.getItem('healthcues_token');
      const backendUrl = import.meta.env.REACT_APP_BACKEND_URL || process.env.REACT_APP_BACKEND_URL;
      await fetch(`${backendUrl}/api/admin/notifications/${notificationId}/dismiss`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` }
      });

      setDismissed([...dismissed, notificationId]);
    } catch (err) {
      console.error('Failed to dismiss notification:', err);
    }
  };

  const handleViewSnapshot = (snapshotId) => {
    // Check if snapshot still exists in notifications (not yet applied/ignored)
    const notification = notifications.find(n => n.snapshotId === snapshotId);
    if (!notification) {
      addToast('This snapshot has already been processed', 'info');
      fetchNotifications(); // Refresh to clear stale notifications
      return;
    }
    setSelectedSnapshotId(snapshotId);
  };

  const visibleNotifications = notifications.filter(n => !dismissed.includes(n.id));

  if (visibleNotifications.length === 0) return null;

  return (
    <>
      <div style={{ marginBottom: 16 }}>
        {visibleNotifications.map(notification => (
          <div
            key={notification.id}
            data-testid="tax-sync-notification-banner"
            style={{
              padding: 16,
              borderRadius: 10,
              background: 'rgb(254, 243, 199)',
              border: '1px solid rgb(217, 119, 6)',
              display: 'flex',
              alignItems: 'flex-start',
              justifyContent: 'space-between',
              gap: 12,
              marginBottom: 12
            }}
          >
            <div style={{ flex: 1, display: 'flex', alignItems: 'flex-start', gap: 12 }}>
              <Icon name="alert-circle" style={{ fontSize: 24, color: 'rgb(217, 119, 6)', flexShrink: 0, marginTop: 2 }} />
              <div style={{ flex: 1 }}>
                <h4 style={{ fontSize: 14, fontWeight: 600, margin: 0, color: 'rgb(120, 53, 15)' }}>
                  Tax Rate Sync Pending Review
                </h4>
                <p style={{ fontSize: 13, color: 'rgb(120, 53, 15)', margin: '4px 0 0 0', lineHeight: 1.4 }}>
                  {notification.message}
                </p>
                <div style={{ marginTop: 8, display: 'flex', gap: 8, fontSize: 12 }}>
                  <span style={{ fontFamily: "'JetBrains Mono', monospace", color: 'rgb(120, 53, 15)' }}>
                    +{notification.summary.added || 0} added • ⟳{notification.summary.updated || 0} updated
                    {notification.summary.skipped > 0 && ` • ⊘${notification.summary.skipped} skipped`}
                  </span>
                </div>
                <button
                  onClick={() => handleViewSnapshot(notification.snapshotId)}
                  style={{
                    marginTop: 12,
                    padding: '6px 14px',
                    borderRadius: 8,
                    border: '1px solid rgb(217, 119, 6)',
                    background: 'white',
                    color: 'rgb(217, 119, 6)',
                    fontSize: 13,
                    fontWeight: 600,
                    cursor: 'pointer',
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 6
                  }}
                >
                  <Icon name="eye-outline" style={{ fontSize: 16 }} />
                  Review & Apply Changes
                </button>
              </div>
            </div>
            <button
              onClick={() => handleDismiss(notification.id)}
              style={{
                padding: 6,
                border: 'none',
                background: 'transparent',
                cursor: 'pointer',
                color: 'rgb(120, 53, 15)',
                borderRadius: 6,
                flexShrink: 0
              }}
              title="Dismiss"
            >
              <Icon name="close-outline" style={{ fontSize: 20 }} />
            </button>
          </div>
        ))}
      </div>

      {selectedSnapshotId && (
        <TaxSnapshotDetailModal
          snapshotId={selectedSnapshotId}
          onClose={() => {
            setSelectedSnapshotId(null);
            fetchNotifications(); // Refresh after closing
          }}
        />
      )}
    </>
  );
}
